/**
 * Les formats de la famille médias — purs, testés. Une absence d'information rend une
 * chaîne vide, jamais « 0:00 » ni « 0 o » : l'appelant n'affiche rien.
 */
import type { Ratio, Sources } from "./types";

/** Le minutage d'un média, en secondes : 42 → « 0:42 » ; 3 725 → « 1:02:05 ». Absent, négatif ou infini → « ».
 *  Pas une durée humaine (« 2 h 05 ») : celle-là est `Temps.formatDuree`, en millisecondes. */
export function formatMinutage(secondes: number | null | undefined): string {
  if (secondes == null || !Number.isFinite(secondes) || secondes < 0) return "";
  const total = Math.floor(secondes);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, "0");
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** 1 536 → « 1,5 Ko » ; 48 × 1024² → « 48 Mo ». En français, base 1024 comme le poste. */
export function formatPoids(octets: number | null | undefined): string {
  if (octets == null || !Number.isFinite(octets) || octets < 0) return "";
  if (octets < 1024) return `${Math.round(octets)} o`;
  const unites = ["Ko", "Mo", "Go", "To"];
  let v = octets / 1024;
  let i = 0;
  while (v >= 1024 && i < unites.length - 1) {
    v /= 1024;
    i += 1;
  }
  const arrondi = v >= 10 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${arrondi.toLocaleString("fr-FR")} ${unites[i]}`;
}

/** Le ratio en valeur CSS `aspect-ratio`. `naturel` (et l'absence) → `undefined`. */
export function ratioCss(ratio: Ratio | undefined): string | undefined {
  if (ratio == null || ratio === "naturel") return undefined;
  if (typeof ratio === "number") return Number.isFinite(ratio) && ratio > 0 ? String(ratio) : undefined;
  return ratio;
}

/** Les sources d'un média en liste, vides retirées. */
export function listeDeSources(src: Sources | undefined): string[] {
  if (src == null) return [];
  return (typeof src === "string" ? [src] : [...src]).filter((s) => s.length > 0);
}

/**
 * Un fichier est-il accepté par un `accept` d'`<input type=file>` ?
 * « image/*,video/mp4,.csv » : les types MIME, les familles `x/*`, les extensions.
 * Un `accept` vide accepte tout.
 */
export function accepteLeFichier(fichier: { name: string; type: string }, accepte: string | undefined): boolean {
  const regles = (accepte ?? "")
    .split(",")
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);
  if (regles.length === 0) return true;
  const type = (fichier.type || "").toLowerCase();
  const nom = fichier.name.toLowerCase();
  return regles.some((r) => {
    if (r.startsWith(".")) return nom.endsWith(r);
    if (r.endsWith("/*")) return type.startsWith(r.slice(0, -1));
    return type === r;
  });
}

/** Pourquoi un fichier est refusé avant même d'être envoyé. */
export type RaisonDuRefus = "type" | "poids";

export interface FichierRefuse {
  fichier: File;
  raison: RaisonDuRefus;
}

/** Trie ce qu'on dépose : ce qui part, et ce qui est refusé (mauvais type, trop lourd). */
export function trierLesFichiers(
  fichiers: readonly File[],
  accepte?: string,
  maxOctets?: number,
): { acceptes: File[]; refuses: FichierRefuse[] } {
  const acceptes: File[] = [];
  const refuses: FichierRefuse[] = [];
  for (const f of fichiers) {
    if (!accepteLeFichier(f, accepte)) refuses.push({ fichier: f, raison: "type" });
    else if (maxOctets != null && f.size > maxOctets) refuses.push({ fichier: f, raison: "poids" });
    else acceptes.push(f);
  }
  return { acceptes, refuses };
}
